import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { SprengnetterValuationHubOneClickModelResponseSoneResponse as SoneResponse } from "@/api/generatedApi";
import { initialTabsStateWithImages as initialPropertyDetails } from "@/pages/oneClickPage/defaultRealEstateData";
import { DataSources, MapboxAddress, RealEstateAddress } from "@/types/oneClick";
import { ProductTypeEnum, PropertyInformation } from "@/types/refineRealEstate";

export type OneClickPropertyDetails = Omit<PropertyInformation, "propertyImages">;
export interface OneClickState {
  address: RealEstateAddress | null;
  dataSources?: DataSources;
  soneValuation?: SoneResponse | null;
  propertyDetails: OneClickPropertyDetails | null;
  isValuationStarted: boolean;
}

const initialState: OneClickState = {
  address: null,
  propertyDetails: initialPropertyDetails,
  dataSources: {},
  soneValuation: null,
  isValuationStarted: false,
};

export const oneClickPageSlice = createSlice({
  name: "oneClickPage",
  initialState,
  reducers: {
    setAddress: (state, action: PayloadAction<MapboxAddress>) => {
      action.payload;
      state.address = {
        nation: action.payload.country || null,
        town: action.payload.city || null,
        street: action.payload.street || null,
        houseNumber: action.payload.houseNumber || null,
        postCode: action.payload.postalCode || null,
        coordinates: action.payload.center ? { lat: action.payload.center[1], lng: action.payload.center[0] } : null,
      };
    },

    updatePropertyDetails: (state, action: PayloadAction<OneClickPropertyDetails>) => {
      if (!state.propertyDetails) {
        state.propertyDetails = {
          generalInfo: {},
          plotData: {},
          buildingData: {},
          energyAndPhotovoltaic: {},
          buildingStandard: {},
          modernization: {},
          financialAndRentalInfo: {},
        } as OneClickPropertyDetails;
      }

      state.propertyDetails = {
        ...state.propertyDetails,
        generalInfo: action.payload.generalInfo
          ? {
              ...(state.propertyDetails?.generalInfo || {}),
              ...action.payload.generalInfo,
            }
          : state.propertyDetails?.generalInfo,
        plotData: action.payload.plotData
          ? {
              ...(state.propertyDetails?.plotData || {}),
              ...action.payload.plotData,
            }
          : state.propertyDetails?.plotData,
        buildingData: action.payload.buildingData
          ? {
              ...(state.propertyDetails?.buildingData || {}),
              ...action.payload.buildingData,
            }
          : state.propertyDetails?.buildingData,
        energyAndPhotovoltaic: action.payload.energyAndPhotovoltaic
          ? {
              ...(state.propertyDetails?.energyAndPhotovoltaic || {}),
              ...action.payload.energyAndPhotovoltaic,
            }
          : state.propertyDetails?.energyAndPhotovoltaic,
        buildingStandard: action.payload.buildingStandard
          ? {
              ...(state.propertyDetails?.buildingStandard || {}),
              ...action.payload.buildingStandard,
            }
          : state.propertyDetails?.buildingStandard,
        modernization: action.payload.modernization
          ? {
              ...(state.propertyDetails?.modernization || {}),
              ...action.payload.modernization,
            }
          : state.propertyDetails?.modernization,
        financialAndRentalInfo: action.payload.financialAndRentalInfo
          ? {
              ...(state.propertyDetails?.financialAndRentalInfo || {}),
              ...action.payload.financialAndRentalInfo,
            }
          : state.propertyDetails?.financialAndRentalInfo,
      };
    },
    setPropertyDataSources: (state, action: PayloadAction<DataSources>) => {
      state.dataSources = {
        ...state.dataSources,
        ...action.payload,
      };
    },
    setSoneValuation: (state, action: PayloadAction<SoneResponse | null>) => {
      state.soneValuation = action.payload;
    },
    setIsValuationStarted: (state, action: PayloadAction<boolean>) => {
      state.isValuationStarted = action.payload;
    },
    setProductType: (state, action: PayloadAction<ProductTypeEnum>) => {
      if (!state.propertyDetails) {
        state.propertyDetails = {
          generalInfo: {},
          plotData: {},
          buildingData: {},
          energyAndPhotovoltaic: {},
          buildingStandard: {},
          modernization: {},
          financialAndRentalInfo: {},
        } as OneClickPropertyDetails;
      }

      state.propertyDetails = {
        ...state.propertyDetails,
        generalInfo: {
          ...(state.propertyDetails?.generalInfo || {}),
          productType: action.payload,
        },
      };
    },
  },
});

export const {
  setAddress,
  updatePropertyDetails,
  setSoneValuation,
  setPropertyDataSources,
  setIsValuationStarted,
  setProductType,
} = oneClickPageSlice.actions;

export const OneClickPageReducer = oneClickPageSlice.reducer;
